import type { MarketGroup, MarketsListData, MarketsListItem } from '@/entities/market';
import type { MarketApps, MarketAppWithTabs, TilesWidgetV2 } from '@/features/dashboard';
import { http } from '@/services/http';
import { getInternalBasePath } from '@/shared/lib/environmentPaths';
import { sanitizeName } from '@/shared/lib/format/sanitizeName';
import { parseMarketComponent } from '../lib/parseMarketComponent';
import type { Market, MarketDto, MarketsPageType } from '../types';

import type { MarketTabWidgets, WidgetItem } from './markets.types';

interface MarketDTO {
  insightId: string;
  metadata: {
    displayName: string;
    commodity: string;
    region: string;
    tag?: string | null;
    comingSoon?: boolean;
  };
}

interface MarketGroupDTO {
  subHeading: string;
  order: number;
  markets: MarketDTO[];
}

type MarketWidgetDefinition = Record<string, unknown> & {
  layout?: unknown;
  tabs?: MarketWidgetDefinitionTab[];
};

type MarketWidgetDefinitionTab = Record<string, unknown> & {
  name?: string;
  type?: string;
};

type MarketWidgetsResponseTab = {
  name: string;
  widgets: Array<{
    type: string;
    definition: MarketWidgetDefinition;
  }>;
};

export const marketsApi = {
  getMarketsList: async (verificationToken?: string): Promise<MarketsListData> => {
    const { data } = await http.internal.get<MarketGroupDTO[]>(
      `${getInternalBasePath()}markets`,
      verificationToken ? { headers: { Authorization: `Bearer ${verificationToken}` } } : undefined
    );

    return {
      marketsList: data.reduce((acc, item) => {
        return [
          ...acc,
          ...item.markets.map(
            (market): MarketsListItem => ({
              marketId: market.insightId.toLowerCase(),
              metadata: {
                displayName: market.metadata.displayName,
                commodity: market.metadata.commodity,
                region: market.metadata.region
              }
            })
          )
        ];
      }, [] as MarketsListItem[]),
      groupedMarkets: data.map(
        (item): MarketGroup => ({
          name: item.subHeading,
          order: item.order,
          markets: item.markets.map(market => ({
            name: market.metadata.displayName,
            tag: market.metadata.tag,
            comingSoon: market.metadata.comingSoon,
            marketId: market.insightId.toLowerCase()
          }))
        })
      )
    };
  },

  getMarket: async (marketId: string): Promise<Market> => {
    const { data } = await http.internal.get<MarketDto>(
      `${getInternalBasePath()}markets/${marketId}`
    );

    const pages = data.pages.map(page => ({
      ...page,
      id: sanitizeName(page.name),
      components: page.components.map(component => {
        return parseMarketComponent(component, data.version);
      })
    }));

    return {
      ...data,
      id: marketId,
      pages
    };
  },

  getMarketWidgets: async (marketId: string): Promise<MarketTabWidgets[]> => {
    const { data } = await http.internal.get<MarketWidgetsResponseTab[]>(
      `${getInternalBasePath()}markets/${marketId}/widgets`
    );

    return data.map(tab => {
      const widgets: WidgetItem[] = [];
      for (const widget of tab.widgets) {
        if (widget.definition.tabs) {
          widget.definition.tabs.forEach(widgetTab => {
            const widgetType =
              widget.type === 'tabcontainer' && widgetTab.type ? widgetTab.type : widget.type;
            widgets.push({
              name: widgetTab.name,
              type: widgetType,
              definition: { ...widgetTab, layout: widget.definition.layout, type: widgetType }
            });
          });
        } else {
          widgets.push(widget);
        }
      }
      return { name: tab.name, widgets };
    });
  },

  previewMarket: async (template: MarketApps): Promise<MarketsPageType | null> => {
    const { data } = await http.internal.post<MarketDto>(
      `${getInternalBasePath()}markets/preview`,
      {
        version: 2,
        pages: [
          {
            name: '',
            components: template
          }
        ]
      }
    );

    const pages: MarketsPageType[] = data.pages.map(page => ({
      ...page,
      id: sanitizeName(page.name),
      components: page.components.map((component, componentIndex) => {
        const parsedComponent = parseMarketComponent(component, '2');
        if (parsedComponent.component?.type === 'tiles') {
          const tilesApp = template[0] as TilesWidgetV2 | undefined;
          return {
            ...parsedComponent,
            component: {
              ...parsedComponent.component,
              tiles: parsedComponent.component.tiles.map((tile, index) => ({
                ...tile,
                id: tilesApp?.tiles[index]?.id ?? tile.id
              }))
            }
          };
        }
        const tabbedApp = template[componentIndex] as Partial<MarketAppWithTabs> | undefined;
        return {
          ...parsedComponent,
          id: template[componentIndex].id,
          tabs: parsedComponent.tabs?.map((tab, index) => ({
            ...tab,
            id: tabbedApp?.tabs?.[index]?.id ?? tab.id
          }))
        };
      })
    }));

    if (pages.length === 0) {
      return null;
    }

    return pages[0];
  }
};
